/**
 * Files module (#93, folders #123): the organization's persisted documents in
 * folders — list per folder, upload into the open folder, open in the Reader,
 * delete (#101), New folder / Rename / Delete folder, Move documents. Renders
 * what the proxy returns, including per-item rights; holds no access rules
 * (dec_c4744b081b). The open folder is the route: #/files, #/files/folder/<id>.
 * The Nextcloud folder (#98 part 3) lives under #/files/nextcloud…: the exposed
 * folders, browsed live through the proxy, New folder only (nextcloud-source).
 * Part 4: load a Nextcloud file into IMSight (analyze it), Load new version of a changed
 * file, Open its stored analysis in the Reader; while a row is analyzing the folder reloads
 * every 5 s, and a file loaded here opens in the Reader once it is ready.
 */

import template from './files.template.html?raw';
import type { AppModule, RouteParams } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';
import type FilesService from '../../../core/files-service';
import type IntegrationsService from '../../../core/integrations-service';
import {
  isIntegrationError,
  type StoredAnalysis,
} from '../../../core/integrations-service';
import type { ConfirmDialogOptions } from '../../../components/confirm-dialog';
import type { NameDialogOptions } from '../../../components/name-dialog';
import type { MoveDialogOptions } from '../../../components/move-dialog';
import {
  FILES_MAX_UPLOAD_BYTES,
  duplicateOf,
  type FileItem,
  type FolderItem,
  type FolderListing,
} from '../../../core/files-service';
import { isNotFound } from '../../../core/auth-utils';
import {
  createFileList,
  type FileListView,
} from '../../../components/file-list';
import { FILE_LOADER } from '../../../core/app-constants';
import { errorMessage } from '../../../core/forbidden-message';
import {
  GatewayErrorCode,
  isGatewayError,
} from '../../../core/gateway/error-codes';
import { nextcloudErrorText } from '../../../components/nextcloud-error-text';
import { replaceQuestion } from '../../../components/replace-question';
import {
  acceptsFilesSubroute,
  folderPathFor,
  mountRow,
  nextcloudAnalysisError,
  nextcloudErrorRoute,
  nextcloudFileRef,
  nextcloudMkdirErrorKey,
  parseFilesSubroute,
  sameRoute,
  type FilesRoute,
} from './nextcloud-source';

export interface FilesModuleDeps {
  t: (key: string, params?: TranslationParams) => string;
  onLanguageChange: (callback: () => void) => void;
  getService: () => FilesService | null;
  /** #140: with the flag on, `source` names the collection document opened. */
  openInReader: (
    file: File,
    source?: { id: string; name: string }
  ) => Promise<void>;
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  nameDialog: (options: NameDialogOptions) => Promise<boolean>;
  moveDialog: (
    options: MoveDialogOptions
  ) => Promise<string | null | undefined>;
  navigate: (path: string) => void;
  enabled: boolean;
  /** #140: VITE_FEATURE_COMPARE. */
  compareEnabled: boolean;
  /** #98 part 3: the Nextcloud folder; null/absent outside proxy mode. */
  getIntegrationsService?: () => IntegrationsService | null;
  /** #98 part 4: show a stored analysis in the Reader (no analysis runs). */
  openStoredAnalysis?: (
    stored: StoredAnalysis,
    source: { name: string; folderId: string; path: string }
  ) => Promise<void>;
}

const PAGE_SIZE = 25;
/** #98 part 4: reload interval while a Nextcloud file is analyzing. */
const POLL_MS = 5000;
const NEXTCLOUD_COLUMNS = {
  by: 'modules.files.colOwner',
  date: 'modules.files.colModified',
};

export function createFilesModule(deps: FilesModuleDeps): AppModule {
  const { t } = deps;
  const collection = deps.compareEnabled;
  const labelKey = collection
    ? 'modules.collection.label'
    : 'modules.files.label';
  let root: HTMLElement | null = null;
  let fileList: FileListView | null = null;
  let listing: FolderListing | null = null;
  let folderId: string | null = null;
  let route: FilesRoute = { kind: 'own', folderId: null };
  /** Shown once on the page a redirect leads to, then cleared on the next route. */
  let pendingMessage: string | null = null;

  const redirect = (text: string, path: string): void => {
    pendingMessage = text;
    say(text);
    fileList?.clear();
    deps.navigate(path);
  };
  const nextcloudAvailable = (): boolean =>
    (deps.getIntegrationsService?.() ?? null) !== null;
  let page = 1;
  let loadSeq = 0;
  /** Between onEnter and onLeave: only then does the folder poll. */
  let visible = false;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Files loaded here (Load into IMSight / Load new version): the Reader opens one once it
   * is ready, as long as the user stays in this folder.
   */
  const awaitingOpen = new Set<string>();
  /** #140: the failed collection document the next picked file replaces (Retry). */
  let retryTarget: string | null = null;
  /** #140: the last Nextcloud listing could not read the collection states. */
  let statesUnavailable = false;

  const $ = <T extends HTMLElement>(sel: string) =>
    root!.querySelector<T>(sel)!;
  const say = (text: string) => ($('[data-files-message]').textContent = text);
  const errorText = (error: unknown) =>
    errorMessage(error, (k) => t(k), t('modules.files.error'));
  const codeOf = (error: unknown) =>
    isGatewayError(error) ? error.code : null;

  /** Another organization's folder: the proxy answers 403 FORBIDDEN_ACTION. */
  const isForeignFolder = (error: unknown): boolean =>
    isGatewayError(error) &&
    error.code === GatewayErrorCode.FORBIDDEN &&
    error.details?.serverCode === 'FORBIDDEN_ACTION';

  /** Text for a failed New folder / Rename, shown inside the Name dialog. */
  function nameError(error: unknown): string {
    if (codeOf(error) === GatewayErrorCode.DUPLICATE_NAME)
      return t('modules.files.folderExists');
    if (codeOf(error) === GatewayErrorCode.INVALID_INPUT)
      return t('modules.files.invalidName');
    if (isNotFound(error)) return t('modules.files.folderGone');
    return errorText(error);
  }

  /** Upload and drop zone only for own documents; New folder where the listing allows. */
  function applyHeader(current: FolderListing | null): void {
    if (!root) return;
    const nextcloud = route.kind === 'nextcloud';
    const noUpload = nextcloud || current?.folder.canUpload === false;
    $('[data-files-upload-button]').hidden = noUpload;
    $('[data-files-drop]').hidden = noUpload;
    $('[data-files-new-folder]').hidden = nextcloud
      ? current?.folder.canCreateFolder !== true
      : current?.folder.canCreateFolder === false;
  }

  /** A quiet load (polling) keeps the rows on screen and ignores a passing failure. */
  async function loadNextcloud(
    nc: Extract<FilesRoute, { kind: 'nextcloud' }>,
    quiet = false
  ): Promise<void> {
    const service = deps.getIntegrationsService?.() ?? null;
    if (!fileList || !service) return;
    const seq = ++loadSeq;
    stopPolling();
    if (!quiet) fileList.showLoading();
    try {
      let result = await service.list(nc.exposedId, nc.path, page, PAGE_SIZE);
      if (seq !== loadSeq) return;
      statesUnavailable = false;
      if (collection) {
        result = await withCollectionStates(service, nc.exposedId, result);
        if (seq !== loadSeq) return;
      }
      listing = result;
      fileList.render(result);
      applyHeader(result);
      const statesText = t('modules.collection.statesUnavailable');
      if (statesUnavailable) say(statesText);
      else if ($('[data-files-message]').textContent === statesText) say('');
      if (nc.exposedId === null && result.total === 0) {
        say(t('modules.files.nextcloud.noFoldersShared'));
      }
      if (!collection) openReady(result);
      schedulePoll();
    } catch (error) {
      if (seq !== loadSeq) return;
      const target = nextcloudErrorRoute(error, nc);
      if (target) {
        listing = null;
        redirect(t(target.messageKey), target.navigate);
        return;
      }
      if (quiet && listing) {
        schedulePoll();
        return;
      }
      listing = null;
      fileList.showError(nextcloudErrorText(error, t));
    }
  }

  /**
   * #140: Nextcloud files carry their collection state; a path the answer lacks is not
   * added. When the states cannot be read, every file shows as not added and
   * statesUnavailable is set (the listing itself still shows).
   */
  async function withCollectionStates(
    service: IntegrationsService,
    exposedId: string | null,
    result: FolderListing
  ): Promise<FolderListing> {
    const files = result.items.filter(
      (item): item is FileItem => item.kind === 'file'
    );
    if (exposedId === null || files.length === 0) return result;
    const paths = files.flatMap((item) => {
      const ref = nextcloudFileRef(item.id);
      return ref ? [ref.path] : [];
    });
    let answer: Awaited<ReturnType<IntegrationsService['collectionStates']>> =
      [];
    try {
      answer = await service.collectionStates(exposedId, paths);
    } catch {
      statesUnavailable = true;
    }
    // The proxy answers a file never added as 'notAdded': no collection entry (#144).
    const states = new Map(
      answer.filter((s) => s.status !== 'notAdded').map((s) => [s.path, s])
    );
    return {
      ...result,
      items: result.items.map((item) => {
        if (item.kind !== 'file') return item;
        const state = states.get(nextcloudFileRef(item.id)?.path ?? '');
        const {
          analysis: _analysis,
          canAnalyze: _canAnalyze,
          canOpen: _canOpen,
          canLoadNewVersion: _canLoadNewVersion,
          ...rest
        } = item;
        return {
          ...rest,
          status: 'external',
          ...(state
            ? {
                collection: {
                  status: state.status,
                  documentId: state.documentId,
                  changed: state.changed,
                },
              }
            : {}),
        };
      }),
    };
  }

  function stopPolling(): void {
    if (pollTimer !== null) clearTimeout(pollTimer);
    pollTimer = null;
  }

  /**
   * Reload in POLL_MS while a row of the open folder is analyzing (Nextcloud) or, with
   * #140, queued/preparing, and the module is shown.
   */
  function schedulePoll(): void {
    stopPolling();
    const busy = listing?.items.some((item) => {
      if (item.kind !== 'file') return false;
      if (!collection) return item.status === 'analyzing';
      const status =
        route.kind === 'nextcloud' ? item.collection?.status : item.status;
      return status === 'queued' || status === 'preparing';
    });
    if (!visible || !busy || (!collection && route.kind !== 'nextcloud'))
      return;
    const current = route;
    pollTimer = setTimeout(() => {
      pollTimer = null;
      if (!visible || route !== current) return;
      // A hidden browser tab asks nothing; the next tick tries again.
      if (document.visibilityState === 'hidden') return schedulePoll();
      void (current.kind === 'nextcloud'
        ? loadNextcloud(current, true)
        : load(true));
    }, POLL_MS);
  }

  /** #140: Add one Nextcloud file to the collection; a name clash asks to replace. Never opens the Reader. */
  async function addToCollection(
    item: FileItem,
    button: HTMLButtonElement
  ): Promise<void> {
    const service = deps.getIntegrationsService?.() ?? null;
    const ref = nextcloudFileRef(item.id);
    if (!service || !ref || route.kind !== 'nextcloud') return;
    const nc = route;
    button.disabled = true;
    say('');
    try {
      let updated: FileItem;
      try {
        updated = await service.addToCollection(ref.folderId, ref.path);
      } catch (error) {
        const existing = duplicateOf(error);
        if (!existing) {
          if (isDuplicate(error)) {
            say(t('modules.files.duplicateUnknown'));
            button.disabled = false;
            return;
          }
          throw error;
        }
        if (!existing.canDelete) {
          say(
            t('modules.files.duplicate', {
              name: existing.uploadedBy?.name ?? '—',
            })
          );
          button.disabled = false;
          return;
        }
        if (!(await deps.confirm(replaceQuestion(t, item.name)))) {
          button.disabled = false;
          return;
        }
        updated = await service.addToCollection(
          ref.folderId,
          ref.path,
          existing.id
        );
      }
      if (route !== nc || !listing) return;
      // A listing already on its way predates this add: drop it.
      loadSeq++;
      listing = {
        ...listing,
        items: listing.items.map((i) =>
          i.kind === 'file' && i.id === item.id
            ? { ...i, collection: updated.collection }
            : i
        ),
      };
      fileList?.render(listing);
      schedulePoll();
    } catch (error) {
      button.disabled = false;
      if (route !== nc) return;
      if (
        isIntegrationError(error) &&
        error.code === 'COLLECTION_UNAVAILABLE'
      ) {
        say(t('modules.collection.unavailable'));
        return;
      }
      analysisFailed(error, nc);
    }
  }

  /** Opens the first file loaded here that finished (the Reader takes over, polling stops on leave). */
  function openReady(current: FolderListing): void {
    for (const item of current.items) {
      if (item.kind !== 'file' || !awaitingOpen.has(item.id)) continue;
      if (item.status === 'analyzing') continue;
      // Finished or failed: either way nothing to wait for any more.
      awaitingOpen.delete(item.id);
      const ready =
        (item.status === 'analyzed' || item.status === 'outdated') &&
        item.canOpen === true;
      if (ready) {
        awaitingOpen.clear();
        void openStored(item);
        return;
      }
    }
  }

  /** Load one Nextcloud file (or a new version of it) into IMSight; its row turns analyzing in place. */
  async function analyzeItem(
    item: FileItem,
    button: HTMLButtonElement
  ): Promise<void> {
    const service = deps.getIntegrationsService?.() ?? null;
    const ref = nextcloudFileRef(item.id);
    if (!service || !ref || route.kind !== 'nextcloud') return;
    const nc = route;
    button.disabled = true;
    say('');
    try {
      const updated = await service.analyze(ref.folderId, ref.path);
      if (route !== nc || !listing) return;
      // A listing already on its way predates this Analyze: drop it.
      loadSeq++;
      listing = {
        ...listing,
        items: listing.items.map((i) => (i.id === updated.id ? updated : i)),
      };
      fileList?.render(listing);
      awaitingOpen.add(updated.id);
      openReady(listing);
      schedulePoll();
    } catch (error) {
      button.disabled = false;
      if (route === nc) analysisFailed(error, nc);
    }
  }

  /** Open a Nextcloud file's stored analysis in the Reader. */
  async function openStored(item: FileItem): Promise<void> {
    const service = deps.getIntegrationsService?.() ?? null;
    const ref = nextcloudFileRef(item.id);
    if (
      !service ||
      !ref ||
      route.kind !== 'nextcloud' ||
      !deps.openStoredAnalysis
    )
      return;
    const nc = route;
    say(t('modules.files.opening', { name: item.name }));
    try {
      const stored = await service.analysis(ref.folderId, ref.path);
      await deps.openStoredAnalysis(stored, { name: item.name, ...ref });
    } catch (error) {
      if (route === nc) analysisFailed(error, nc);
    }
  }

  function analysisFailed(
    error: unknown,
    nc: Extract<FilesRoute, { kind: 'nextcloud' }>
  ): void {
    const target = nextcloudAnalysisError(error);
    if (target?.navigate) {
      redirect(t(target.messageKey), target.navigate);
      return;
    }
    say(target ? t(target.messageKey) : nextcloudErrorText(error, t));
    if (target?.reload) {
      // Keep the message: a reload does not clear it.
      void loadNextcloud(nc, true);
    }
  }

  /** The Nextcloud row for the own root, page 1, while the connection is active. */
  async function nextcloudRow(): Promise<FolderItem | null> {
    const integrations = deps.getIntegrationsService?.() ?? null;
    if (!integrations || folderId !== null || page !== 1) return null;
    try {
      const status = await integrations.status();
      return status.state === 'active'
        ? mountRow(t('modules.files.nextcloudRoot'))
        : null;
    } catch {
      return null;
    }
  }

  async function load(quiet = false): Promise<void> {
    if (!fileList) return;
    if (route.kind === 'nextcloud') return loadNextcloud(route, quiet);
    const service = deps.getService();
    if (!service) {
      // No proxy: nothing to request; show the empty root.
      listing = {
        folder: { id: null, name: '', canRename: false, canDelete: false },
        path: [],
        items: [],
        page: 1,
        pageSize: PAGE_SIZE,
        total: 0,
      };
      fileList.render(listing);
      return;
    }
    const seq = ++loadSeq;
    stopPolling();
    if (!quiet) fileList.showLoading();
    const row = nextcloudRow();
    try {
      let result = await service.list(folderId, page, PAGE_SIZE);
      if (seq !== loadSeq) return;
      if (result.items.length === 0 && result.page > 1) {
        // The last item of the final page went away: step back one page.
        page = result.page - 1;
        result = await service.list(folderId, page, PAGE_SIZE);
        if (seq !== loadSeq) return;
      }
      const mount = await row;
      if (seq !== loadSeq) return;
      if (mount) result = { ...result, items: [mount, ...result.items] };
      listing = result;
      fileList.render(result);
      applyHeader(result);
      schedulePoll();
    } catch (error) {
      if (seq !== loadSeq) return;
      if (quiet && listing) {
        schedulePoll();
        return;
      }
      listing = null;
      if (folderId !== null && (isNotFound(error) || isForeignFolder(error))) {
        redirect(t('modules.files.folderGone'), '/files');
        return;
      }
      fileList.showError(errorText(error));
    }
  }

  async function newNextcloudFolder(
    nc: Extract<FilesRoute, { kind: 'nextcloud' }>
  ): Promise<void> {
    const integrations = deps.getIntegrationsService?.() ?? null;
    const exposedId = nc.exposedId;
    if (!integrations || exposedId === null) return;
    const created = await deps.nameDialog({
      title: t('modules.files.newFolderTitle'),
      label: t('modules.files.folderName'),
      initial: '',
      saveLabel: t('modules.files.create'),
      cancelLabel: t('common.cancel'),
      submit: async (name) => {
        try {
          await integrations.mkdir(exposedId, nc.path, name);
          return null;
        } catch (error) {
          const key = nextcloudMkdirErrorKey(error);
          return key ? t(key) : nextcloudErrorText(error, t);
        }
      },
    });
    if (created) await load();
  }

  async function newFolder(): Promise<void> {
    if (route.kind === 'nextcloud') return newNextcloudFolder(route);
    const service = deps.getService();
    if (!service) return;
    const parentId = folderId;
    const created = await deps.nameDialog({
      title: t('modules.files.newFolderTitle'),
      label: t('modules.files.folderName'),
      initial: '',
      saveLabel: t('modules.files.create'),
      cancelLabel: t('common.cancel'),
      submit: async (name) => {
        try {
          await service.mkdir(parentId, name);
          return null;
        } catch (error) {
          return nameError(error);
        }
      },
    });
    if (created) await load();
  }

  async function renameFolder(item: FolderItem): Promise<void> {
    const service = deps.getService();
    if (!service) return;
    const renamed = await deps.nameDialog({
      title: t('modules.files.renameTitle'),
      label: t('modules.files.folderName'),
      initial: item.name,
      saveLabel: t('modules.files.rename'),
      cancelLabel: t('common.cancel'),
      submit: async (name) => {
        try {
          await service.renameFolder(item.id, name);
          return null;
        } catch (error) {
          return nameError(error);
        }
      },
    });
    if (renamed) await load();
  }

  async function deleteFolder(
    item: FolderItem,
    button: HTMLButtonElement
  ): Promise<void> {
    const service = deps.getService();
    if (!service || button.disabled) return;
    button.disabled = true;
    try {
      if (
        !(await deps.confirm({
          title: t('modules.files.deleteFolderTitle'),
          message: t('modules.files.confirmDeleteFolder', { name: item.name }),
          confirmLabel: t('modules.files.delete'),
          cancelLabel: t('common.cancel'),
          danger: true,
        }))
      ) {
        button.disabled = false;
        return;
      }
      await service.deleteFolder(item.id);
      say('');
      await load();
    } catch (error) {
      button.disabled = false;
      if (codeOf(error) === GatewayErrorCode.FOLDER_NOT_EMPTY) {
        say(t('modules.files.folderNotEmpty'));
        return;
      }
      if (isNotFound(error) || outcomeUnknown(error)) {
        await load();
        return;
      }
      say(errorText(error));
    }
  }

  async function moveItem(item: FileItem): Promise<void> {
    const service = deps.getService();
    if (!service) return;
    const target = await deps.moveDialog({
      title: t('modules.files.moveTitle', { name: item.name }),
      rootLabel: t(labelKey),
      moveHereLabel: t('modules.files.moveHere'),
      cancelLabel: t('common.cancel'),
      loadingText: t('modules.files.moveLoading'),
      noFoldersText: t('modules.files.moveNoFolders'),
      currentFolderId: folderId,
      listFolders: (id) => service.list(id, 1, 100),
      errorText,
    });
    if (target === undefined || target === folderId) return;
    try {
      try {
        await service.move(item.id, target);
      } catch (error) {
        const existing = duplicateOf(error);
        if (!existing) throw error;
        if (!existing.canDelete) {
          say(
            t('modules.files.moveDuplicate', {
              name: existing.uploadedBy?.name ?? '—',
            })
          );
          return;
        }
        if (
          !(await deps.confirm({
            title: t('modules.files.replaceTitle'),
            message: t('modules.files.confirmMoveReplace', { name: item.name }),
            confirmLabel: t('modules.files.replace'),
            cancelLabel: t('modules.files.keepExisting'),
            danger: false,
          }))
        ) {
          return;
        }
        await service.move(item.id, target, existing.id);
      }
      say('');
      await load();
    } catch (error) {
      if (isNotFound(error)) {
        say(t('modules.files.folderGone'));
        await load();
        return;
      }
      if (outcomeUnknown(error)) {
        await load();
        return;
      }
      say(errorText(error));
    }
  }

  async function openItem(item: FileItem): Promise<void> {
    const service = deps.getService();
    if (!service) return;
    say(t('modules.files.opening', { name: item.name }));
    try {
      await deps.openInReader(
        await service.download(item.id),
        ...(collection ? [{ id: item.id, name: item.name }] : [])
      );
      say('');
    } catch (error) {
      say(errorText(error));
    }
  }

  async function deleteItem(
    item: FileItem,
    button: HTMLButtonElement
  ): Promise<void> {
    const service = deps.getService();
    if (!service || button.disabled) return;
    button.disabled = true;
    try {
      if (
        !(await deps.confirm({
          title: t('modules.files.deleteTitle'),
          message: t('modules.files.confirmDelete', { name: item.name }),
          confirmLabel: t('modules.files.delete'),
          cancelLabel: t('common.cancel'),
          danger: true,
        }))
      ) {
        button.disabled = false;
        return;
      }
      await service.delete(item.id);
      say('');
      await load();
    } catch (error) {
      button.disabled = false;
      if (isGatewayError(error) && error.details?.status === 502) {
        await load();
        return;
      }
      say(errorText(error));
    }
  }

  function isDuplicate(error: unknown): boolean {
    return (
      isGatewayError(error) && error.code === GatewayErrorCode.DUPLICATE_NAME
    );
  }

  /** 502 or a network failure: the upload may or may not have been stored. */
  function outcomeUnknown(error: unknown): boolean {
    return isGatewayError(error)
      ? error.details?.status === 502
      : error instanceof TypeError;
  }

  /**
   * #140: replaceId (Retry on a failed collection document) uploads the first
   * file in that document's place.
   */
  async function uploadFiles(files: File[], replaceId?: string): Promise<void> {
    // No uploads into Nextcloud (overview decision: New folder is the only write).
    if (route.kind === 'nextcloud') return;
    const service = deps.getService();
    if (!service) return;
    const targetFolder = folderId;
    const list = $('[data-files-uploads]');
    let uploaded = false;
    let unknownOutcome = false;
    for (const file of files) {
      const li = document.createElement('li');
      li.textContent = file.name;
      list.append(li);
      if (file.size > FILES_MAX_UPLOAD_BYTES) {
        li.textContent = `${file.name}: ${t('modules.files.tooLarge')}`;
        continue;
      }
      if (!FILE_LOADER.EXTENSION_PATTERN.test(file.name)) {
        li.textContent = `${file.name}: ${t('modules.files.unsupported')}`;
        continue;
      }
      li.textContent = `${file.name}: ${t('modules.files.uploading')}`;
      try {
        try {
          await service.upload(
            file,
            'eng',
            replaceId,
            targetFolder,
            ...(collection ? [true] : [])
          );
        } catch (error) {
          if (replaceId && isNotFound(error)) {
            li.textContent = `${file.name}: ${t('modules.files.replaceGone')}`;
            unknownOutcome = true;
            continue;
          }
          if (isDuplicate(error) && !duplicateOf(error)) {
            li.textContent = `${file.name}: ${t('modules.files.duplicateUnknown')}`;
            continue;
          }
          const existing = duplicateOf(error);
          if (!existing) throw error;
          if (!existing.canDelete) {
            li.textContent = `${file.name}: ${t('modules.files.duplicate', {
              name: existing.uploadedBy?.name ?? '—',
            })}`;
            continue;
          }
          if (!(await deps.confirm(replaceQuestion(t, file.name)))) {
            li.textContent = `${file.name}: ${t('modules.files.skipped')}`;
            continue;
          }
          li.textContent = `${file.name}: ${t('modules.files.uploading')}`;
          try {
            await service.upload(
              file,
              'eng',
              existing.id,
              targetFolder,
              ...(collection ? [true] : [])
            );
          } catch (replaceError) {
            // The proxy answers 404 when the document to replace is gone.
            if (isNotFound(replaceError)) {
              li.textContent = `${file.name}: ${t('modules.files.replaceGone')}`;
              unknownOutcome = true;
              continue;
            }
            throw replaceError;
          }
        }
        li.remove();
        uploaded = true;
      } catch (error) {
        li.textContent = `${file.name}: ${errorText(error)}`;
        if (outcomeUnknown(error)) unknownOutcome = true;
      }
    }
    if (uploaded || unknownOutcome) {
      page = 1;
      await load();
    }
  }

  return {
    id: 'files',
    route: '/files',
    labelKey,
    descriptionKey: collection
      ? 'modules.collection.description'
      : 'modules.files.description',
    icon: 'files',
    enabled: deps.enabled,
    showOnDashboard: true,
    acceptsSubroute: (subroute) =>
      acceptsFilesSubroute(subroute, nextcloudAvailable()),
    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      fileList = createFileList({
        t,
        rootLabelKey: labelKey,
        emptyRootKey: 'modules.files.empty',
        emptyFolderKey: 'modules.files.emptyFolder',
        handlers: {
          folderHref: (id) => `#${folderPathFor(route, id)}`,
          onOpen: (item) => void openItem(item),
          onMove: (item) => void moveItem(item),
          onDeleteFile: (item, button) => void deleteItem(item, button),
          onRenameFolder: (item) => void renameFolder(item),
          onDeleteFolder: (item, button) => void deleteFolder(item, button),
          onAnalyze: (item, button) => void analyzeItem(item, button),
          ...(collection
            ? {
                onAddToCollection: (
                  item: FileItem,
                  button: HTMLButtonElement
                ) => void addToCollection(item, button),
              }
            : {}),
          ...(collection
            ? {
                onRetryUpload: (item: FileItem) => {
                  retryTarget = item.id;
                  $<HTMLInputElement>('[data-files-input]').click();
                },
              }
            : {}),
          onOpenStored: (item) => void openStored(item),
          onRetry: () => void load(),
          onPage: (p) => {
            page = p;
            void load();
          },
        },
      });
      $('[data-files-crumbs-slot]').replaceWith(fileList.breadcrumbs);
      $('[data-files-list-slot]').replaceWith(fileList.body);
      const section = $('.files-module');
      section.setAttribute('aria-label', t(labelKey));
      const uploadButton = $('[data-files-upload-button]');
      const applyUploadLabel = (): void => {
        if (!collection) return;
        uploadButton.dataset.i18n = 'modules.collection.add';
        uploadButton.textContent = t('modules.collection.add');
      };
      applyUploadLabel();
      const input = $<HTMLInputElement>('[data-files-input]');
      $('[data-files-upload-button]').addEventListener('click', () => {
        retryTarget = null;
        input.click();
      });
      input.addEventListener('cancel', () => {
        retryTarget = null;
      });
      $('[data-files-new-folder]').addEventListener(
        'click',
        () => void newFolder()
      );
      input.addEventListener('change', () => {
        const replaceId = retryTarget;
        retryTarget = null;
        const picked = [...(input.files ?? [])];
        void (replaceId
          ? uploadFiles(picked.slice(0, 1), replaceId)
          : uploadFiles(picked));
        input.value = '';
      });
      const drop = $('[data-files-drop]');
      drop.addEventListener('dragover', (e) => {
        e.preventDefault();
        drop.classList.add('is-over');
      });
      drop.addEventListener('dragleave', () =>
        drop.classList.remove('is-over')
      );
      drop.addEventListener('drop', (e) => {
        e.preventDefault();
        drop.classList.remove('is-over');
        void uploadFiles([...(e.dataTransfer?.files ?? [])]);
      });
      deps.onLanguageChange(() => {
        if (!root) return;
        section.setAttribute('aria-label', t(labelKey));
        applyUploadLabel();
        if (listing) fileList?.render(listing);
      });
    },
    onLeave() {
      visible = false;
      stopPolling();
      awaitingOpen.clear();
    },
    onEnter(params: RouteParams) {
      visible = true;
      const parsed = parseFilesSubroute(params?.subroute ?? null);
      if ('fallback' in parsed) {
        redirect(t('modules.files.nextcloud.folderGone'), parsed.fallback);
        return;
      }
      const next = parsed.route;
      if (next.kind === 'nextcloud' && !nextcloudAvailable()) {
        deps.navigate('/files');
        return;
      }
      if (!sameRoute(next, route)) {
        awaitingOpen.clear();
        page = 1;
        listing = null;
        fileList?.clear();
        if (root) say(pendingMessage ?? '');
        pendingMessage = null;
      }
      route = next;
      folderId = next.kind === 'own' ? next.folderId : null;
      fileList?.setEmptyKeys(
        next.kind === 'nextcloud'
          ? {
              root: 'modules.files.emptyFolder',
              folder: 'modules.files.nextcloud.emptyFolder',
            }
          : undefined
      );
      fileList?.setColumns(
        next.kind === 'nextcloud' ? NEXTCLOUD_COLUMNS : undefined
      );
      applyHeader(null);
      void load();
    },
  };
}
